/** /users/:id/:tab? — the user record (plan 2026-10-03-console-user-record-page.md, "Page
 *  anatomy"): a person record on the records core's page shape — the booking and order
 *  records' sibling.
 *
 *  - **Header** — `UserRecordHeader`: avatar, name, kind chip, a state chip only when not
 *    plainly active; tenant · email · phone · ID; on the right the Actions menu and Edit
 *    (`E`) / Close editor (`Esc`).
 *  - **Tabs** (URL) — `useRecordNav` + `RecordPageTabs`: real links (cmd-click opens a
 *    tab) that keep `?edit`; the default tab (Profile) has no path segment. Visibility by
 *    kind and permission; an old link to a folded tab lands on the tab that absorbed it
 *    (`TAB_ALIASES`).
 *  - **Related records** — the strip keeps only the grids no tab already shows.
 *  - **Profile** — the account band, then Trips and the registry's cards
 *    (`UserProfileView`), the only editable surface.
 *  - **Edit mode** — exactly the record editor: `?edit=1` (or `E`) flips the cards'
 *    FieldSlots and docks the ledger; the draft (`DraftProvider kind="user"`) and the
 *    editing session (`RecordEditorProvider`) are PAGE-level, so switching tabs keeps
 *    both, the ledger stays docked on every tab, and the unsaved-changes guard only fires
 *    on leaving the record (`basePath=/users/<id>`). Preview → Save → receipt with the
 *    audit link; a 409 turns the drifted fields stale (Keep mine / Take theirs); the feed
 *    verdicts over the user's open bookings mark the fields and the ledger rows. No
 *    console_user_manage, or the kill switch off → view only; a deleted user → Edit
 *    disabled with the reason. A draft restored from sessionStorage
 *    (`console:draft:user:<id>`) — or kept after leaving edit mode — is announced in view
 *    mode too.
 *  - **States** — skeleton panels while loading; the records core's empty state for a
 *    missing user (404, or an id that is not one) or one the operator may not see (403);
 *    a deleted user carries the banner with Restore (and why Edit is disabled). */
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Alert, Box, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, type MutableRefObject, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useUserEditContext, useUserRecordCatalog, type UserRecordCatalog, type UserRecordEditContext } from '@/api/userRecord';
import * as usersApi from '@/api/users';
import type { ConsoleUserDetail, UserKind } from '@/api/users';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { RecordHeaderActions } from '@/features/records/header/RecordHeaderActions';
import { Ledger } from '@/features/records/ledger/Ledger';
import { LedgerDock } from '@/features/records/ledger/LedgerDock';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  missingStatus,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { usePermissions } from '@/hooks/usePermissions';
import { formatZoned } from '@/utils/zonedTime';

import { AiDiagnosePanel } from './diagnose/AiDiagnosePanel';
import { USER_MANAGE_PERM, USER_TIMEZONE, userEditBlockedReason, useUserRecordEditorConfig } from './record/editorConfig';
import { useUserImmediateActions } from './record/useUserImmediateActions';
import { UserProfileBand, UserProfileView, useProfileTrips } from './record/UserProfileView';
import { UserRecordHeader } from './record/UserRecordHeader';
import { focusAnchor } from './record/reveal';
import { DeletedUserBanner, UserLoading, UserNoPermission, UserNotFound } from './record/UserRecordStates';
import { userSectionAnchorId, type UserRecordData } from './record/views/userLayout';
import { RelatedRecordsStrip } from './RelatedRecordsStrip';
import { AuditTab } from './tabs/AuditTab';
import { AuthenticationTab, INVITATION_ANCHOR_ID } from './tabs/AuthenticationTab';
import { BookingsTab } from './tabs/BookingsTab';
import { PermissionsContainer } from './tabs/PermissionsContainer';
import { SessionsTab } from './tabs/SessionsTab';
import { gridKindOf, relatedRecordActions } from './userLinks';

export type UserTabId =
  | 'profile'
  | 'authentication'
  | 'permissions'
  | 'sessions'
  | 'audit'
  | 'bookings'
  | 'history'
  | 'diagnose';

type TabDef = {
  id: UserTabId;
  label: string;
  kinds: UserKind[];
  /** Console permission required to see the tab, beyond the route's own
   *  `console_user_view`. Declared here rather than as another hardcoded clause
   *  in the filter below (the BookingDetailPage pattern). */
  perm?: string;
};

// Tab visibility by kind. Profile/Sessions/Audit are universal;
// Authentication is system+staff (guests use magic links) and carries the
// system-only console invite.
const ALL_TABS: TabDef[] = [
  { id: 'profile', label: 'Profile', kinds: ['system', 'staff', 'guest'] },
  { id: 'authentication', label: 'Authentication', kinds: ['system', 'staff'] },
  { id: 'permissions', label: 'Permissions', kinds: ['system', 'staff', 'guest'] },
  { id: 'sessions', label: 'Sessions', kinds: ['system', 'staff', 'guest'] },
  { id: 'audit', label: 'Audit Log', kinds: ['system', 'staff', 'guest'] },
  // Staff and system users reach bookings through staff roles, so this is not
  // guest-only. Behind the bookings permission: the grid is the /bookings
  // surface with a pinned user.
  {
    id: 'bookings',
    label: 'Bookings',
    kinds: ['system', 'staff', 'guest'],
    perm: 'console_bookings_view',
  },
  // Field-level object-change history (who changed what, incl. import-path
  // guest-data corrections) — distinct from the Audit Log's console events.
  { id: 'history', label: 'Change history', kinds: ['system', 'staff', 'guest'], perm: 'console_audit_view' },
  // AI diagnostics over the account's full evidence bundle (perm-gated:
  // console_entity_ai — exposes logs + PII and spends LLM budget).
  { id: 'diagnose', label: 'Diagnose', kinds: ['system', 'staff', 'guest'], perm: 'console_entity_ai' },
];

/** Tabs folded into another tab: an old link lands on the one that absorbed it — the
 *  Invitations tab into Authentication, the Contacts tab into the Profile's Contacts card
 *  (D6). */
const TAB_ALIASES: Record<string, UserTabId> = { invitations: 'authentication', contacts: 'profile' };

const TABS_LABELS = { label: 'User sections', refreshingLabel: 'Refreshing the user' };

/** The tabs a viewer sees on a user of `kind`, in order. */
export function userTabs(kind: UserKind, hasPerm: (perm: string) => boolean): { id: UserTabId; label: string }[] {
  return ALL_TABS.filter((t) => t.kinds.includes(kind) && (!t.perm || hasPerm(t.perm))).map(({ id, label }) => ({
    id,
    label,
  }));
}

/** Where an old link to a folded tab lands inside the tab that absorbed it: the Contacts
 *  card, the Authentication tab's invitation section. */
const ALIAS_ANCHORS: Record<string, string> = {
  contacts: userSectionAnchorId('contacts'),
  invitations: INVITATION_ANCHOR_ID,
};
/** How long an alias waits for its section to render (the cards wait for the editor's data). */
const ALIAS_WAIT_MS = 5000;
const ALIAS_POLL_MS = 100;

/** An old tab link (`/users/<id>/contacts`, `/invitations`) scrolls to and focuses the
 *  section that absorbed the tab — once it has rendered and nothing above it is still
 *  loading (a Trips skeleton settling would move it after the scroll); a section that never
 *  renders (an invitation section only a system user has) leaves the page at the tab's top. */
function useAliasReveal(segment: string | undefined) {
  useEffect(() => {
    const id = segment ? ALIAS_ANCHORS[segment] : undefined;
    if (!id) return undefined;
    const started = Date.now();
    const timer = window.setInterval(() => {
      const late = Date.now() - started > ALIAS_WAIT_MS;
      const settled = !document.querySelector('[data-user-profile] [role="status"]');
      if (((settled || late) && focusAnchor(id)) || late) window.clearInterval(timer);
    }, ALIAS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [segment]);
}

/** The tab a URL segment opens: itself, the tab that absorbed it, else the first tab. */
export function resolveUserTab(segment: string | undefined, tabs: { id: UserTabId }[]): UserTabId {
  const requested = segment ? (TAB_ALIASES[segment] ?? segment) : undefined;
  return tabs.find((t) => t.id === requested)?.id ?? tabs[0]?.id ?? 'profile';
}

export function UserDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) return <UserNotFound id={idStr ?? ''} />;
  return <UserRecordPage key={id} id={id} tabParam={tab} />;
}

function UserRecordPage({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const nav = useRecordNav<UserTabId>(`/users/${id}`, 'profile');
  const { hasPerm } = usePermissions();

  const detailQ = useQuery({
    queryKey: usersApi.userDetailKey(id),
    queryFn: () => usersApi.retrieve(id),
    retry: retryUnlessMissing,
  });
  const missing = missingStatus(detailQ.error);
  const catalogQ = useUserRecordCatalog();
  const ctxQ = useUserEditContext(id, { enabled: missing === null });
  const user = detailQ.data;

  const tabs = useMemo(() => (user ? userTabs(user.kind, hasPerm) : []), [user, hasPerm]);
  const activeTab = resolveUserTab(tabParam, tabs);
  useAliasReveal(tabParam);

  if (missing === 404) return <UserNotFound id={id} />;
  if (missing === 403) return <UserNoPermission id={id} />;
  if (detailQ.isLoading) return <UserLoading id={id} />;
  if (detailQ.error || !user) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void detailQ.refetch()}>
            Retry
          </Button>
        }
      >
        Couldn’t load user #{id}
        {detailQ.error instanceof Error && detailQ.error.message ? ` — ${detailQ.error.message}` : ''}.
      </Alert>
    );
  }

  const shell: ShellProps = {
    user,
    tabs,
    activeTab,
    nav,
    fetching: detailQ.isFetching || ctxQ.isFetching,
    onChanged: () => void detailQ.refetch(),
    editorError: catalogQ.error ?? ctxQ.error ?? null,
    retryEditor: () => {
      void catalogQ.refetch();
      void ctxQ.refetch();
    },
  };
  const catalog = catalogQ.data;
  const editContext = ctxQ.data;
  if (!catalog || !editContext) return <UserRecordShell {...shell} data={null} />;
  return <UserRecordEditor {...shell} catalog={catalog} editContext={editContext} />;
}

interface ShellProps {
  user: ConsoleUserDetail;
  tabs: { id: UserTabId; label: string }[];
  activeTab: UserTabId;
  nav: RecordNav<UserTabId>;
  fetching: boolean;
  onChanged: () => void;
  editorError: unknown;
  retryEditor: () => void;
}

interface PendingFocus {
  rec: string;
  path: string;
}

/** The page under the user editor: the draft and the editing session, page-level. */
function UserRecordEditor({
  catalog,
  editContext,
  ...shell
}: ShellProps & { catalog: UserRecordCatalog; editContext: UserRecordEditContext }) {
  const { user, nav } = shell;
  const { hasPerm } = usePermissions();
  const [params] = useSearchParams();
  // A ledger row for a field that is not on screen (another tab): back to Profile, then
  // focus it once its FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(shell.activeTab);
  tabRef.current = shell.activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: string, path: string) => {
      if (tabRef.current === 'profile') return;
      pendingFocus.current = { rec, path };
      goTab('profile');
    },
    [goTab],
  );
  const config = useUserRecordEditorConfig({ catalog, editContext, detail: user, onFocusMissing });
  const data = useMemo(() => ({ catalog, editContext, detail: user }), [catalog, editContext, user]);
  const blocked = userEditBlockedReason(catalog, editContext);
  const mayManage = hasPerm(USER_MANAGE_PERM) && editContext.permissions[USER_MANAGE_PERM];
  const canEdit = mayManage && blocked === null;
  const editing = canEdit && params.get('edit') === '1';
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);
  return (
    <DraftProvider kind="user" recordId={user.id} baselines={editContext.baselines} baselineTs={editContext.generated_ts}>
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <UserEditorShell
          {...shell}
          data={data}
          canEdit={canEdit}
          editing={editing}
          // A deleted user: Edit stays in view, disabled, with the reason. The kill switch is
          // the Actions menu's note.
          editBlocked={mayManage && editContext.is_deleted ? blocked : null}
          note={!catalog.enabled || !editContext.flags.user_edit_enabled ? blocked : null}
          pendingFocus={pendingFocus}
        />
      </RecordEditorProvider>
    </DraftProvider>
  );
}

function UserEditorShell({
  canEdit,
  editing,
  editBlocked,
  note,
  pendingFocus,
  ...shell
}: ShellProps & {
  data: UserRecordData;
  canEdit: boolean;
  editing: boolean;
  editBlocked: string | null;
  note: string | null;
  pendingFocus: MutableRefObject<PendingFocus | null>;
}) {
  const { user, nav, activeTab, tabs } = shell;
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  const { prompt } = useDraftUnsavedGuard(`/users/${user.id}`);
  const { setParam, goTab } = nav;
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);
  const immediate = useUserImmediateActions({
    user,
    dirtyCount: count,
    onDiagnose: tabs.some((t) => t.id === 'diagnose') ? () => goTab('diagnose') : undefined,
  });

  useRecordShortcuts({ ...(canEdit && !editing ? { onEdit: enterEdit } : {}) }, { stageCount: 0 });

  const onProfile = activeTab === 'profile';
  useEffect(() => {
    if (!onProfile || !pendingFocus.current) return undefined;
    // Taken when the frame runs, not before: an editor update in between (a preview
    // arriving) re-runs this effect, whose cleanup cancels the frame — the pending focus
    // must survive that to be tried again.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onProfile, editor, pendingFocus]);

  const draftBanner =
    !editing && count > 0 ? (
      <Alert
        severity="info"
        icon={<EditNoteOutlinedIcon />}
        sx={{ mb: 2 }}
        data-view-draft-banner
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canEdit && (
              <Button color="inherit" size="small" onClick={enterEdit}>
                Continue editing
              </Button>
            )}
            <Button
              color="inherit"
              size="small"
              onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}
            >
              Discard
            </Button>
          </Box>
        }
      >
        {restored
          ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, USER_TIMEZONE, { weekday: false })} — not saved yet.`
          : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
      </Alert>
    ) : null;

  return (
    <UserRecordShell
      {...shell}
      headerActions={
        <RecordHeaderActions
          canEdit={canEdit}
          editing={editing}
          editBlocked={editBlocked}
          note={note}
          groups={immediate.groups}
          onEdit={enterEdit}
          onClose={editor.requestClose}
        />
      }
      draftBanner={draftBanner}
      ledgerOpen={editing}
      trailing={
        <>
          {immediate.host}
          {prompt}
        </>
      }
    />
  );
}

function UserRecordShell({
  user,
  tabs,
  activeTab,
  nav,
  fetching,
  onChanged,
  editorError,
  retryEditor,
  data,
  headerActions,
  draftBanner,
  ledgerOpen,
  trailing,
}: ShellProps & {
  data: UserRecordData | null;
  headerActions?: ReactNode;
  draftBanner?: ReactNode;
  /** The ledger docks beside the content (edit mode); undefined = no editor. */
  ledgerOpen?: boolean;
  trailing?: ReactNode;
}) {
  const { hasPerm } = usePermissions();
  const canViewAudit = hasPerm('console_audit_view');
  const tabIds = useMemo(() => new Set<string>(tabs.map((t) => t.id)), [tabs]);
  const linkCtx = { tenantId: user.tour_operator?.id ?? null, kind: gridKindOf(user.kind) };
  const trips = useProfileTrips(user);
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  const { hrefFor } = nav;
  const profileHref = useCallback((tab: UserTabId) => hrefFor(tab), [hrefFor]);
  const onProfile = activeTab === 'profile';

  let content: ReactNode = null;
  switch (activeTab) {
    case 'profile':
      content = (
        <UserProfileView
          trips={trips}
          data={data}
          dataError={editorError}
          onRetry={retryEditor}
          width={contentWidth}
          editing={ledgerOpen === true}
          tabs={tabIds}
          hrefFor={profileHref}
        />
      );
      break;
    case 'authentication':
      content = <AuthenticationTab user={user} onChanged={onChanged} />;
      break;
    case 'permissions':
      content = <PermissionsContainer user={user} onChanged={onChanged} />;
      break;
    case 'sessions':
      content = <SessionsTab userId={user.id} />;
      break;
    case 'audit':
      content = <AuditTab userId={user.id} userEmail={user.email} canViewAudit={canViewAudit} />;
      break;
    case 'bookings':
      content = <BookingsTab userId={user.id} />;
      break;
    case 'history':
      content = <HistoryPanel contentType="users.user" objectPk={String(user.id)} onRestored={onChanged} />;
      break;
    case 'diagnose':
      content = <AiDiagnosePanel userId={user.id} />;
      break;
  }

  return (
    <Box data-record-page={user.id}>
      <UserRecordHeader
        user={user}
        section={tabs.find((t) => t.id === activeTab)?.label ?? 'Profile'}
        {...(headerActions ? { actions: headerActions } : {})}
      />

      {user.is_deleted && (
        <DeletedUserBanner userId={user.id} canRestore={hasPerm('console_user_manage')} onRestored={onChanged} />
      )}
      {draftBanner}

      <RelatedRecordsStrip actions={relatedRecordActions(user, linkCtx, { canViewAudit, tabs: tabIds })} />

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={fetching} {...TABS_LABELS} />

      {onProfile && <UserProfileBand trips={trips} tabs={tabIds} hrefFor={profileHref} />}

      <Box ref={contentRef}>
        {ledgerOpen === undefined ? (
          content
        ) : (
          <LedgerDock
            open={ledgerOpen}
            stickyTop={DOCK_GAP_PX - SCROLLER_PADDING_PX}
            viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
            ledger={(variant) => <Ledger variant={variant} />}
          >
            {content}
          </LedgerDock>
        )}
      </Box>
      {trailing}
    </Box>
  );
}
